/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useDarkMode } from "../../context/DarkModeContext";
import {
  Bookmark,
  BookmarkCheck,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  SkeletonJobListing,
  SkeletonJobSearch,
  SkeletonPagination,
} from "../../components/Skeleton";

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  description: string;
  category: string;
  deadline?: string;
  createdAt: string;
  postedBy: {
    id: string;
    name: string;
    email: string;
  };
  _count?: {
    jobApplications: number;
    jobBookmarks: number;
  };
}

interface Application {
  id: string;
  jobId: string;
  userId: string;
  status: "pending" | "accepted" | "rejected";
  appliedAt: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalJobs: number;
  hasNext: boolean;
  hasPrev: boolean;
  pageSize: number;
}

export default function FindJob() {
  const location = useLocation();
  const [highlightedJobId, setHighlightedJobId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [jobs, setJobs] = useState<Job[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bookmarkLoading, setBookmarkLoading] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalJobs: 0,
    hasNext: false,
    hasPrev: false,
    pageSize: 10,
  });

  const {
    user,
    bookmarks,
    addBookmark,
    removeBookmark,
    isJobBookmarked,
    fetchBookmarks,
  } = useAuth();
  const { isDark } = useDarkMode();

  const navigate = useNavigate();

  const API_BASE = import.meta.env.VITE_API_BASE;

  // Handle highlighted job from navigation state
  useEffect(() => {
    if (location.state?.highlightedJobId) {
      const jobId = location.state.highlightedJobId;
      setHighlightedJobId(jobId);

      // Clear the state to prevent highlighting on refresh
      window.history.replaceState({}, document.title);

      // Auto-clear highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedJobId(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Auto-scroll to highlighted job
  useEffect(() => {
    if (highlightedJobId && jobs.length > 0) {
      const timer = setTimeout(() => {
        const highlightedElement = document.getElementById(
          `job-${highlightedJobId}`,
        );
        if (highlightedElement) {
          highlightedElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [highlightedJobId, jobs]);

  // Fetch jobs from backend with pagination
  const fetchJobs = async (page: number = 1) => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      // Build query parameters with safe defaults
      const params = new URLSearchParams({
        page: page?.toString() || "1",
        limit: (pagination.pageSize || 10).toString(),
      });

      // Add search filter if provided
      if (search) {
        params.append("search", search);
      }

      // Add job type filter if not 'all'
      if (filter !== "all") {
        params.append("type", filter);
      }

      const response = await fetch(`${API_BASE}/jobs?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch jobs");
      }

      const data = await response.json();

      // Handle both response formats: { jobs: [...], pagination: {...} } or direct array
      if (data.jobs && data.pagination) {
        // New format with pagination
        setJobs(Array.isArray(data.jobs) ? data.jobs : []);
        setPagination(data.pagination);
      } else if (data.jobs) {
        // Legacy format with jobs array
        setJobs(Array.isArray(data.jobs) ? data.jobs : []);
        setPagination((prev) => ({
          ...prev,
          currentPage: 1,
          totalPages: Math.ceil(data.jobs.length / (prev.pageSize || 10)), // Safe division
          totalJobs: data.jobs.length,
          hasNext: false,
          hasPrev: false,
        }));
      } else {
        // Direct array
        const jobsArray = Array.isArray(data) ? data : [];
        setJobs(jobsArray);
        setPagination((prev) => ({
          ...prev,
          currentPage: 1,
          totalPages: Math.ceil(jobsArray.length / (prev.pageSize || 10)), // Safe division
          totalJobs: jobsArray.length,
          hasNext: false,
          hasPrev: false,
        }));
      }
    } catch (err: any) {
      console.error("Error fetching jobs:", err);
      setError(err.message || "An error occurred while fetching jobs");
      setJobs([]);
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
        totalPages: 1,
        totalJobs: 0,
        hasNext: false,
        hasPrev: false,
        pageSize: prev.pageSize || 10, // Ensure pageSize is set
      }));
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's applications
  const fetchApplications = async () => {
    if (!user) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE}/jobs/applications/my-applications`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || data);
      }
    } catch (err) {
      console.error("Error fetching applications:", err);
    }
  };

  useEffect(() => {
    fetchJobs(1); // Always start from page 1 on component mount
    if (user) {
      fetchApplications();
      fetchBookmarks();
    }
  }, [user]);

  // Fetch jobs when search or filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchJobs(1); // Reset to page 1 when search/filter changes
    }, 500); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [search, filter]);

  // Toggle bookmark - now uses context functions
  const toggleBookmark = async (jobId: string) => {
    if (!user) {
      alert("Please log in to save jobs");
      return;
    }

    setBookmarkLoading(jobId);
    try {
      const token = localStorage.getItem("token");
      const currentlyBookmarked = isJobBookmarked(jobId);

      if (currentlyBookmarked) {
        // Remove bookmark - CORRECTED ENDPOINT
        const response = await fetch(`${API_BASE}/jobs/${jobId}/bookmark`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          removeBookmark(jobId); // Update context
        } else {
          throw new Error("Failed to remove bookmark");
        }
      } else {
        // Add bookmark - CORRECTED ENDPOINT
        const response = await fetch(`${API_BASE}/jobs/${jobId}/bookmark`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          addBookmark(data.bookmark); // Update context
        } else {
          throw new Error("Failed to add bookmark");
        }
      }
    } catch (err: any) {
      console.error("Error toggling bookmark:", err);
      alert(err.message || "An error occurred while saving the job");
    } finally {
      setBookmarkLoading(null);
    }
  };

  // Pagination handlers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchJobs(page);
    }
  };

  const nextPage = () => {
    if (pagination.hasNext) {
      goToPage(pagination.currentPage + 1);
    }
  };

  const prevPage = () => {
    if (pagination.hasPrev) {
      goToPage(pagination.currentPage - 1);
    }
  };

  const goToFirstPage = () => {
    goToPage(1);
  };

  const goToLastPage = () => {
    goToPage(pagination.totalPages);
  };

  // Generate page numbers for pagination controls
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const current = pagination.currentPage;
    const total = pagination.totalPages;

    if (total <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Show pages with ellipsis
      if (current <= 3) {
        // Near the start
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(total);
      } else if (current >= total - 2) {
        // Near the end
        pages.push(1);
        pages.push("...");
        for (let i = total - 3; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push(1);
        pages.push("...");
        for (let i = current - 1; i <= current + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(total);
      }
    }

    return pages;
  };

  const handleApply = (jobId: string) => {
    if (!user) {
      alert("Please log in to apply for jobs");
      return;
    }

    // Navigate to application form with job ID in the dashboard
    navigate(`/dashboard/apply/${jobId}`);
  };

  const getApplicationStatus = (jobId: string): Application | null => {
    return applications.find((app) => app.jobId === jobId) || null;
  };

  const isJobExpired = (job: Job): boolean => {
    if (!job.deadline) return false;
    return new Date(job.deadline) < new Date();
  };

  const getJobTypeDisplay = (type: string) => {
    const typeMap: { [key: string]: string } = {
      full_time: "Full-time",
      part_time: "Part-time",
      contract: "Contract",
      freelance: "Freelance",
      internship: "Internship",
      remote: "Remote",
      temporary: "Temporary",
    };
    return typeMap[type] || type;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        light: "bg-yellow-100 text-yellow-800",
        dark: "bg-yellow-900/30 text-yellow-300",
        text: "Pending",
      },
      accepted: {
        light: "bg-green-100 text-green-800",
        dark: "bg-green-900/30 text-green-300",
        text: "Accepted",
      },
      rejected: {
        light: "bg-red-100 text-red-800",
        dark: "bg-red-900/30 text-red-300",
        text: "Rejected",
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          isDark ? config.dark : config.light
        }`}
      >
        {config.text}
      </span>
    );
  };

  return (
    <div
      className={`min-h-screen py-16 transition-colors duration-300 ${
        isDark
          ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
          : "bg-gradient-to-br from-sky-50 via-blue-50 to-sky-50"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1
            className={`text-4xl font-bold mb-3 text-center bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent transition-all duration-300 ${
              isDark ? "from-sky-400 to-blue-400" : ""
            }`}
          >
            Find Your Next Job
          </h1>
          <p
            className={`text-center text-lg transition-colors duration-300 ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Browse through the latest job opportunities and apply today.
          </p>
        </div>

        {/* Search + Filter - Show skeleton when loading */}
        {loading ? (
          <SkeletonJobSearch theme={isDark ? "dark" : "light"} />
        ) : (
          <div className="flex flex-col md:flex-row items-center gap-4 mb-10">
            <input
              type="text"
              placeholder="Search job title, company, or location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full md:w-2/3 px-4 py-3 border-2 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all duration-300 ${
                isDark
                  ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500/30"
                  : "border-sky-200 text-gray-900 placeholder-gray-500 focus:border-sky-500 focus:ring-sky-500/20"
              }`}
            />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className={`w-full md:w-1/3 px-4 py-3 border-2 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all duration-300 ${
                isDark
                  ? "bg-gray-800 border-gray-600 text-white focus:border-sky-500 focus:ring-sky-500/30"
                  : "border-sky-200 text-gray-900 focus:border-sky-500 focus:ring-sky-500/20"
              }`}
            >
              <option value="all">All Types</option>
              <option value="remote">Remote</option>
              <option value="full_time">Full-time</option>
              <option value="part_time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
            </select>
          </div>
        )}

        {/* Highlighted Job Notification */}
        {highlightedJobId && (
          <div
            className={`mb-6 p-4 rounded-lg border-2 animate-pulse transition-all duration-300 ${
              isDark
                ? "border-yellow-500 bg-gradient-to-r from-yellow-900/30 to-yellow-800/20 text-yellow-200"
                : "border-yellow-400 bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-lg mr-2">⭐</span>
                <p className="font-semibold">
                  Your saved job is highlighted below!
                </p>
              </div>
              <button
                onClick={() => setHighlightedJobId(null)}
                className={`text-lg font-bold transition-colors hover:opacity-70 ${
                  isDark ? "text-yellow-300" : "text-yellow-600"
                }`}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        {!loading && jobs.length > 0 && (
          <div className="text-center mb-6">
            <p
              className={`transition-colors duration-300 ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Showing {jobs.length} of {pagination.totalJobs} jobs
              {search && ` for "${search}"`}
              {filter !== "all" && ` in ${getJobTypeDisplay(filter)}`}
              {` (Page ${pagination.currentPage} of ${pagination.totalPages})`}
            </p>
            {user && bookmarks.length > 0 && (
              <p
                className={`text-sm mt-2 transition-colors duration-300 ${
                  isDark ? "text-sky-400" : "text-sky-600"
                }`}
              >
                You have {bookmarks.length} saved job
                {bookmarks.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        )}

        {/* Loading Skeletons */}
        {loading && (
          <>
            <div className="text-center mb-8">
              <div className="inline-block relative">
                <div
                  className={`w-16 h-16 rounded-full border-4 ${
                    isDark ? "border-gray-700" : "border-sky-100"
                  }`}
                ></div>
                <div
                  className={`absolute top-0 left-0 w-16 h-16 rounded-full border-4 border-t-sky-500 border-r-transparent border-b-transparent border-l-transparent animate-spin`}
                ></div>
              </div>
              <p
                className={`mt-4 transition-colors duration-300 ${
                  isDark ? "text-gray-300" : "text-gray-500"
                }`}
              >
                Loading jobs...
              </p>
            </div>

            {/* Job Listings Skeleton */}
            <SkeletonJobListing
              items={6}
              theme={isDark ? "dark" : "light"}
              layout="grid"
            />

            {/* Pagination Skeleton */}
            <SkeletonPagination theme={isDark ? "dark" : "light"} />
          </>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center mb-8">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                isDark
                  ? "bg-red-900/20 text-red-400"
                  : "bg-red-100 text-red-600"
              }`}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p
              className={`mb-4 text-lg transition-colors duration-300 ${
                isDark ? "text-red-400" : "text-red-600"
              }`}
            >
              {error}
            </p>
            <button
              onClick={() => fetchJobs(1)}
              className={`px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 ${
                isDark
                  ? "bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-700 hover:to-blue-800 text-white"
                  : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
              }`}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Job Listings */}
        {!loading && !error && jobs.length > 0 && (
          <>
            <SkeletonJobListing
              items={0}
              theme={isDark ? "dark" : "light"}
              layout="grid"
            />

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {jobs.map((job) => {
                const application = getApplicationStatus(job.id);
                const isBookmarked = isJobBookmarked(job.id);
                const isExpired = isJobExpired(job);
                const canApply = !application && !isExpired && user;
                const isHighlighted = highlightedJobId === job.id;

                return (
                  <div
                    key={job.id}
                    className={`rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-500 border-2 flex flex-col h-full relative group ${
                      isHighlighted
                        ? isDark
                          ? "ring-4 ring-yellow-500 bg-gradient-to-br from-gray-800 to-gray-900 border-yellow-600 transform scale-[1.02]"
                          : "ring-4 ring-yellow-500 bg-gradient-to-br from-white to-sky-50 border-yellow-400 transform scale-[1.02]"
                        : isDark
                          ? isExpired
                            ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 opacity-75 hover:border-gray-600"
                            : "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 hover:border-sky-600"
                          : isExpired
                            ? "bg-gradient-to-br from-white to-sky-50 border-gray-300 opacity-75 hover:border-gray-400"
                            : "bg-gradient-to-br from-white to-sky-50 border-sky-200 hover:border-sky-400"
                    }`}
                    id={`job-${job.id}`}
                  >
                    {/* Highlight indicator badge */}
                    {isHighlighted && (
                      <div
                        className={`absolute -top-3 -left-2 text-xs font-bold px-3 py-1 rounded-full z-10 shadow-lg animate-pulse ${
                          isDark
                            ? "bg-gradient-to-r from-yellow-600 to-yellow-700 text-white"
                            : "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white"
                        }`}
                      >
                        ⭐ SAVED JOB
                      </div>
                    )}

                    {/* Bookmark Button */}
                    <button
                      onClick={() => toggleBookmark(job.id)}
                      disabled={!user || bookmarkLoading === job.id}
                      className={`absolute top-4 right-4 p-2 rounded-full transition-all duration-300 group-hover:scale-110 ${
                        isBookmarked
                          ? isDark
                            ? "bg-gradient-to-r from-sky-900 to-blue-900 text-sky-400 hover:from-sky-800 hover:to-blue-800"
                            : "bg-gradient-to-r from-sky-100 to-blue-100 text-sky-600 hover:from-sky-200 hover:to-blue-200"
                          : isDark
                            ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-400 hover:from-gray-600 hover:to-gray-700 hover:text-gray-300"
                            : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 hover:from-gray-200 hover:to-gray-300 hover:text-gray-600"
                      } ${
                        bookmarkLoading === job.id
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      } ${isHighlighted ? "scale-110 ring-2 ring-yellow-400" : ""}`}
                      title={
                        isBookmarked ? "Remove from saved jobs" : "Save job"
                      }
                    >
                      {bookmarkLoading === job.id ? (
                        <div
                          className={`w-5 h-5 rounded-full animate-spin border-2 ${
                            isDark
                              ? "border-sky-500 border-t-transparent"
                              : "border-sky-600 border-t-transparent"
                          }`}
                        ></div>
                      ) : isBookmarked ? (
                        <BookmarkCheck size={20} className="fill-current" />
                      ) : (
                        <Bookmark size={20} />
                      )}
                    </button>

                    <div className="flex-1">
                      {/* Application Status Badge */}
                      {application && (
                        <div className="flex justify-between items-start mb-4">
                          <div
                            className={`text-sm transition-colors duration-300 ${
                              isDark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Applied {formatDate(application.appliedAt)}
                          </div>
                          {getStatusBadge(application.status)}
                        </div>
                      )}

                      {isExpired && (
                        <div className="mb-4">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              isDark
                                ? "bg-gradient-to-r from-red-900/30 to-red-800/30 text-red-300"
                                : "bg-gradient-to-r from-red-100 to-red-200 text-red-800"
                            }`}
                          >
                            Expired
                          </span>
                        </div>
                      )}

                      <h2
                        className={`text-2xl font-semibold mb-3 pr-12 transition-colors duration-300 group-hover:text-sky-600 ${
                          isDark
                            ? "text-white group-hover:text-sky-400"
                            : "text-gray-800"
                        } ${isHighlighted ? (isDark ? "text-yellow-400" : "text-yellow-600") : ""}`}
                      >
                        {job.title}
                      </h2>
                      <p
                        className={`font-bold mb-3 bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent transition-all duration-300 group-hover:from-sky-700 group-hover:to-blue-700 ${
                          isDark
                            ? "from-sky-400 to-blue-500 group-hover:from-sky-300 group-hover:to-blue-400"
                            : ""
                        } ${isHighlighted ? (isDark ? "from-yellow-400 to-yellow-500" : "from-yellow-500 to-yellow-600") : ""}`}
                      >
                        {job.company}
                      </p>

                      <div className="space-y-3 mb-4">
                        <p
                          className={`flex items-center transition-colors duration-300 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span
                            className={`mr-2 transition-colors duration-300 ${
                              isDark ? "text-sky-400" : "text-sky-600"
                            }`}
                          >
                            📍
                          </span>
                          {job.location}
                        </p>
                        <p
                          className={`flex items-center transition-colors duration-300 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span
                            className={`mr-2 transition-colors duration-300 ${
                              isDark ? "text-sky-400" : "text-sky-600"
                            }`}
                          >
                            💰
                          </span>
                          {job.salary || "Salary not specified"}
                        </p>
                        <p
                          className={`flex items-center transition-colors duration-300 ${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          <span
                            className={`mr-2 transition-colors duration-300 ${
                              isDark ? "text-sky-400" : "text-sky-600"
                            }`}
                          >
                            ⏰
                          </span>
                          {getJobTypeDisplay(job.type)}
                        </p>

                        {/* Application Deadline */}
                        {job.deadline && (
                          <p
                            className={`text-sm flex items-center transition-colors duration-300 ${
                              isExpired
                                ? isDark
                                  ? "text-red-400"
                                  : "text-red-600"
                                : isDark
                                  ? "text-gray-400"
                                  : "text-gray-500"
                            }`}
                          >
                            <span className="mr-2">⏳</span>
                            Apply by {formatDate(job.deadline)}
                            {isExpired && " (Closed)"}
                          </p>
                        )}

                        {job.createdAt && (
                          <p
                            className={`text-sm transition-colors duration-300 ${
                              isDark ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Posted {formatDate(job.createdAt)}
                          </p>
                        )}
                      </div>

                      {job.description && (
                        <p
                          className={`text-sm mb-4 line-clamp-3 transition-colors duration-300 ${
                            isDark ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {job.description}
                        </p>
                      )}

                      {/* Job Category */}
                      {job.category && (
                        <div className="mb-4">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-sm transition-colors duration-300 ${
                              isDark
                                ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300"
                                : "bg-gradient-to-r from-sky-100 to-blue-100 text-sky-700"
                            }`}
                          >
                            {job.category}
                          </span>
                        </div>
                      )}

                      {/* Applications Count */}
                      {job._count?.jobApplications !== undefined && (
                        <p
                          className={`text-sm mb-2 transition-colors duration-300 ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {job._count.jobApplications} application
                          {job._count.jobApplications !== 1 ? "s" : ""}
                        </p>
                      )}
                    </div>

                    {/* Apply Button */}
                    <button
                      onClick={() => handleApply(job.id)}
                      disabled={!canApply}
                      className={`w-full px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 mt-4 group/apply ${
                        canApply
                          ? isHighlighted
                            ? "bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white"
                            : isDark
                              ? "bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-700 hover:to-blue-800 text-white"
                              : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
                          : application
                            ? isDark
                              ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-400 cursor-not-allowed"
                              : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 cursor-not-allowed"
                            : isExpired
                              ? isDark
                                ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-400 cursor-not-allowed"
                                : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 cursor-not-allowed"
                              : isDark
                                ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-400 cursor-not-allowed"
                                : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      {application
                        ? `Applied - ${
                            application.status.charAt(0).toUpperCase() +
                            application.status.slice(1)
                          }`
                        : isExpired
                          ? "Application Closed"
                          : !user
                            ? "Login to Apply"
                            : "Apply Now"}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8">
                {/* Page Info */}
                <div
                  className={`text-sm transition-colors duration-300 ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Showing page {pagination.currentPage} of{" "}
                  {pagination.totalPages} ({pagination.totalJobs} total jobs)
                </div>

                {/* Pagination Buttons */}
                <div className="flex items-center gap-2">
                  {/* First Page */}
                  <button
                    onClick={goToFirstPage}
                    disabled={pagination.currentPage === 1}
                    className={`p-2 rounded-lg border transition-all duration-300 ${
                      pagination.currentPage === 1
                        ? isDark
                          ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-500 cursor-not-allowed"
                          : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 cursor-not-allowed"
                        : isDark
                          ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300 hover:from-gray-600 hover:to-gray-700 hover:text-white"
                          : "bg-gradient-to-r from-white to-gray-50 text-gray-700 hover:from-sky-50 hover:to-blue-50 hover:text-sky-700"
                    }`}
                  >
                    <ChevronsLeft size={16} />
                  </button>

                  {/* Previous Page */}
                  <button
                    onClick={prevPage}
                    disabled={!pagination.hasPrev}
                    className={`p-2 rounded-lg border transition-all duration-300 ${
                      !pagination.hasPrev
                        ? isDark
                          ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-500 cursor-not-allowed"
                          : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 cursor-not-allowed"
                        : isDark
                          ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300 hover:from-gray-600 hover:to-gray-700 hover:text-white"
                          : "bg-gradient-to-r from-white to-gray-50 text-gray-700 hover:from-sky-50 hover:to-blue-50 hover:text-sky-700"
                    }`}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((page, index) => (
                      <button
                        key={index}
                        onClick={() =>
                          typeof page === "number" ? goToPage(page) : null
                        }
                        disabled={typeof page !== "number"}
                        className={`min-w-[40px] px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-300 ${
                          typeof page !== "number"
                            ? isDark
                              ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-500 cursor-default"
                              : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 cursor-default"
                            : page === pagination.currentPage
                              ? isDark
                                ? "bg-gradient-to-r from-sky-600 to-blue-700 text-white border-sky-600"
                                : "bg-gradient-to-r from-sky-500 to-blue-600 text-white border-sky-500"
                              : isDark
                                ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300 hover:from-gray-600 hover:to-gray-700 hover:text-white border-gray-600"
                                : "bg-gradient-to-r from-white to-gray-50 text-gray-700 hover:from-sky-50 hover:to-blue-50 hover:text-sky-700 border-gray-300"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  {/* Next Page */}
                  <button
                    onClick={nextPage}
                    disabled={!pagination.hasNext}
                    className={`p-2 rounded-lg border transition-all duration-300 ${
                      !pagination.hasNext
                        ? isDark
                          ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-500 cursor-not-allowed"
                          : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 cursor-not-allowed"
                        : isDark
                          ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300 hover:from-gray-600 hover:to-gray-700 hover:text-white"
                          : "bg-gradient-to-r from-white to-gray-50 text-gray-700 hover:from-sky-50 hover:to-blue-50 hover:text-sky-700"
                    }`}
                  >
                    <ChevronRight size={16} />
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={goToLastPage}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className={`p-2 rounded-lg border transition-all duration-300 ${
                      pagination.currentPage === pagination.totalPages
                        ? isDark
                          ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-500 cursor-not-allowed"
                          : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 cursor-not-allowed"
                        : isDark
                          ? "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300 hover:from-gray-600 hover:to-gray-700 hover:text-white"
                          : "bg-gradient-to-r from-white to-gray-50 text-gray-700 hover:from-sky-50 hover:to-blue-50 hover:text-sky-700"
                    }`}
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* No Results */}
        {!loading &&
          !error &&
          jobs.length === 0 &&
          pagination.totalJobs > 0 && (
            <div className="text-center mt-10">
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                  isDark
                    ? "bg-gradient-to-r from-gray-800 to-gray-900 text-gray-400"
                    : "bg-gradient-to-r from-sky-100 to-blue-100 text-gray-500"
                }`}
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p
                className={`text-xl mb-4 transition-colors duration-300 ${
                  isDark ? "text-gray-300" : "text-gray-500"
                }`}
              >
                No jobs match your search criteria
              </p>
              <button
                onClick={() => {
                  setSearch("");
                  setFilter("all");
                  fetchJobs(1);
                }}
                className={`px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 ${
                  isDark
                    ? "bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-700 hover:to-blue-800 text-white"
                    : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
                }`}
              >
                Clear Filters
              </button>
            </div>
          )}

        {/* No Jobs Available */}
        {!loading && !error && pagination.totalJobs === 0 && (
          <div className="text-center mt-10">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                isDark
                  ? "bg-gradient-to-r from-gray-800 to-gray-900 text-gray-400"
                  : "bg-gradient-to-r from-sky-100 to-blue-100 text-gray-500"
              }`}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            </div>
            <p
              className={`text-xl mb-2 transition-colors duration-300 ${
                isDark ? "text-gray-300" : "text-gray-500"
              }`}
            >
              No jobs available at the moment
            </p>
            <p
              className={`transition-colors duration-300 ${
                isDark ? "text-gray-400" : "text-gray-400"
              }`}
            >
              Check back later for new opportunities
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
